import superagent from 'superagent';
import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {mergeWithProperty} from '../util/mergeWithProperty';

const defaultNormalizedDecision = {
	id: '',
	isIncluded: false,
	isExcluded: false,
	canBeIncluded: true,
	canBeExcluded: true,
};
const defaultNormalizedFeature = {
	id: '',
	isSatisfied: false,
	selectedValues: []
};
const defaultNormalizedExplanation = {
	id: '',
	causes: []
};
export const fetchDecisions = createAsyncThunk(
	'decisions/decisionsLoading',
	(options, {getState}) => superagent
		.get(`${getState().status.engineUrl}/session/configuration/decision`)
		.set('X-SESSION-ID', getState().session.sessionId)
		.set('Accept', 'application/json')
		.then((response) => response.body)
		.catch((error) => error)
);
export const fetchConsequences = createAsyncThunk(
	'decisions/consequencesLoading',
	(options, {getState}) => superagent
		.get(`${getState().status.engineUrl}/session/configuration/consequence`)
		.set('X-SESSION-ID', getState().session.sessionId)
		.set('Accept', 'application/json')
		.then((response) => response.body)
		.catch((error) => error)
);
export const decision = createAsyncThunk(
	'decisions/decision',
	async (options, {getState}) => {

		const now = new Date().getTime();
		const absoluteSecondsLeft = (getState().session.timeout.absolute - now) / 1000;
		const slidingSecondsLeft = (getState().session.timeout.time - now) / 1000;
		if (absoluteSecondsLeft > 0 && slidingSecondsLeft > 0) {
			// if session is not expired, put decision
			return superagent
				.put(`${getState().status.engineUrl}/session/configuration/decision/feature/${options.variableId}/value/${encodeURIComponent(options.featureValueId)}`)
				.send({state: options.state})
				.set('X-SESSION-ID', getState().session.sessionId)
				.set('Accept', 'application/json')
				.then((response) => response.body)
				.catch((error) => error);
		} else {
			// else deliver neutral response
			// middleware will handle session recovery and put decision
			return new Promise((resolve) => resolve(
				{
					consequences: {features: [], attributes: []},
					affectedDecisions: {features: [], attributes: []}
				}
			));
		}
	}
);
export const decisions = createAsyncThunk(
	'decisions/decisions',
	async (options, {getState}) => superagent
		.put(`${getState().status.engineUrl}/session/configuration/decision?conflictHandling=AutoResolve`)
		.send(options)
		.set('X-SESSION-ID', getState().session.sessionId)
		.set('Accept', 'application/json')
		.then((response) => response.body)
		.catch((error) => error)
);
export const fetchExplanations = createAsyncThunk(
	'decisions/explanationsLoading',
	(options, {getState}) => superagent
		.get(`${getState().status.engineUrl}/session/configuration/consequence/explanation/feature`)
		.set('X-SESSION-ID', getState().session.sessionId)
		.set('Accept', 'application/json')
		.then((response) => response.body)
		.catch((error) => error)
);
export const decisionsSlice = createSlice({
	name: 'decisions',
	initialState: {
		status: 'idle',
		data: {
			decisions: [],
			normalizedDecisions: {},
			normalizedFeatures: {},
			rejectedDecisions: [],
			isSatisfied: false,
			explanations: [],
			normalizedExplanations: {},
		},
		error: {},
	},
	reducers: {
		clearRejectedDecisions: state => {
			state.data.rejectedDecisions = [];
		},
		resetDecisions: state => {
			state.data = {
				decisions: [],
				normalizedDecisions: {},
				normalizedFeatures: {},
				rejectedDecisions: [],
				isSatisfied: false,
				explanations: [],
				normalizedExplanations: {},
			};
		}
	},
	extraReducers: (builder) => {
		builder.addCase(decision.fulfilled, (state, action) => {
			// we are only ever given a diff, not a complete featureModel
			// apply the results of our decision to the featureModel
			state.data.decisions = mergeWithProperty(
				state.data.decisions,
				action.payload.affectedDecisions ? action.payload.affectedDecisions.features : [],
				'variableId'
			);
			state.data.decisions = getFilteredDecisions(state.data.decisions);
			state.data.isSatisfied = action.payload.consequences ? action.payload.consequences.isSatisfied : false;
			applyDecisions(action.payload.affectedDecisions ? action.payload.affectedDecisions.features : [], state.data);
			applyConsequences(action.payload.consequences ? action.payload.consequences.features : [], state.data);
		});
		builder.addCase(decisions.fulfilled, (state, action) => {
			// store rejected decisions for later processing
			state.data.rejectedDecisions = action.payload.rejectedDecisions.features;
		});
		builder.addCase(fetchDecisions.fulfilled, (state, action) => {
			// apply the results of our decision to the featureModel
			state.data.decisions = mergeWithProperty(
				state.data.decisions,
				action.payload.features,
				'variableId'
			);
			state.data.decisions = getFilteredDecisions(state.data.decisions);
			state.status = 'idle';
			state.error = {};
			applyDecisions(action.payload.features, state.data);
		});
		builder.addCase(fetchConsequences.fulfilled, (state, action) => {
			// apply the results of our decision to the featuremodel
			state.data.isSatisfied = action.payload.isSatisfied;
			state.status = 'idle';
			state.error = {};
			applyConsequences(action.payload.features, state.data);
		});
		builder.addCase(fetchExplanations.fulfilled, (state, action) => {
			state.data.explanations = action.payload;
			state.status = 'idle';
			state.error = {};
			normalizeExplanations(action.payload, state.data);
		});
	}
});

function applyDecisions(rawDecisions, data) {
	rawDecisions.forEach(feature => {
		if (!(feature.variableId in data.normalizedFeatures)) data.normalizedFeatures[feature.variableId] = {...defaultNormalizedFeature};
		data.normalizedFeatures[feature.variableId].id = feature.variableId;

		feature.featureValues.forEach(featureValue => {
			const valueKey = feature.variableId + '.' + featureValue.featureValueId;
			if (!(valueKey in data.normalizedDecisions)) data.normalizedDecisions[valueKey] = {...defaultNormalizedDecision};
			data.normalizedDecisions[valueKey].id = valueKey;
			data.normalizedDecisions[valueKey].isIncluded = featureValue.state === 'Included';
			data.normalizedDecisions[valueKey].isExcluded = featureValue.state === 'Excluded';
		});
		data.normalizedFeatures[feature.variableId].selectedValues = feature.featureValues
			.filter(featureValue => featureValue.state === 'Included')
			.map(featureValue => featureValue.featureValueId);
	});
}

function applyConsequences(rawConsequences, data) {
	rawConsequences.forEach(feature => {
		if (!(feature.variableId in data.normalizedFeatures)) data.normalizedFeatures[feature.variableId] = {...defaultNormalizedFeature};
		data.normalizedFeatures[feature.variableId].id = feature.variableId;
		data.normalizedFeatures[feature.variableId].isSatisfied = feature.isSatisfied;

		feature.featureValues.forEach(featureValue => {
			const valueKey = feature.variableId + '.' + featureValue.featureValueId;
			if (!(valueKey in data.normalizedDecisions)) data.normalizedDecisions[valueKey] = {...defaultNormalizedDecision};
			data.normalizedDecisions[valueKey].id = valueKey;
			data.normalizedDecisions[valueKey].canBeIncluded = featureValue.possibleDecisionStates.includes('Included');
			data.normalizedDecisions[valueKey].canBeExcluded = featureValue.possibleDecisionStates.includes('Excluded');
		});
	});
}

function normalizeExplanations(rawExplanations, data) {
	data.normalizedExplanations = {};
	rawExplanations.forEach(feature => {
		feature.featureValues.forEach(featureValue => {
			const valueKey = feature.variableId + '.' + featureValue.featureValueId;
			if (!(valueKey in data.normalizedExplanations)) data.normalizedExplanations[valueKey] = {...defaultNormalizedExplanation};
			data.normalizedExplanations[valueKey].id = valueKey;
			const causes = [];
			featureValue.explanations.forEach(explanation => {
				explanation.causedByFeatureDecisions.forEach(cause => {
					cause.featureValues.forEach(featureValue2 => {
						const valueKey2 = cause.variableId + '.' + featureValue2.featureValueId;
						causes.push(valueKey2);
					});
				});
			});
			data.normalizedExplanations[valueKey].causes = causes;
		});
	});
}

// reduce decisions to minimal set t be stored
// only features, that contain decisions and featureValues that are not undefined
function getFilteredDecisions(decs) {
	return decs
		.filter(dec => dec.featureValues.find(fv => fv.state === 'Included' && (fv.kind === 'Explicit' || fv.kind === 'Implicit')))
		.map(dec => (
				{
					variableId: dec.variableId,
					featureValues: dec.featureValues.filter(fv => fv.state !== 'Undefined')
				}
			)
		);
}

export const {clearRejectedDecisions, resetDecisions} = decisionsSlice.actions;


export default decisionsSlice.reducer;
